import {DataTestAttrs, getTestProps} from '@benchling/js-test-utils';
import {createStyles, Menu} from '@mantine/core';
import React from 'react';

import {ReviewSummary} from 'constants/SourceConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {ReviewSummaryStateKey, TasksReviewSummaryCountKey} from './MyWorkConstants';
import {TasksReviewSummary} from './MyWorkUtils';

const useStyles = createStyles((theme, {isSelected}: {isSelected: boolean}) => ({
  icon: {
    '&&': {
      width: '16px',
      height: '16px',
    },
    color: isSelected ? theme.colors.blue[6] : theme.colors.gray[3],
  },
}));

type StatusMenuCountParams =
  | {statusCounts: TasksReviewSummary | null; summaryKey: TasksReviewSummaryCountKey; isTask: true}
  | {statusCounts: ReviewSummary | null; summaryKey: ReviewSummaryStateKey; isTask: false};

function getStatusMenuCount(params: StatusMenuCountParams): string {
  if (params.statusCounts == null) {
    return '';
  }
  // Branches look identical but are required by ts compiler: `isTask` narrows `statusCounts`/`summaryKey`
  // so each index access type-checks against TasksReviewSummary vs ReviewSummary.
  return params.isTask
    ? ` (${params.statusCounts[params.summaryKey]})`
    : ` (${params.statusCounts[params.summaryKey]})`;
}

type MyWorkStatusMenuItemProps = {
  isSelected: boolean;
  setIsSelected: (selected: boolean) => void;
  statusLabel: string;
  dataTestAttrs: DataTestAttrs;
} & StatusMenuCountParams;

export function MyWorkStatusMenuItem({
  isSelected,
  setIsSelected,
  statusLabel,
  dataTestAttrs,
  ...props
}: MyWorkStatusMenuItemProps): JSX.Element {
  const {classes} = useStyles({isSelected});

  const statusMenuCount = getStatusMenuCount(props);

  return (
    <Menu.Item
      key={statusLabel}
      aria-label={`${statusLabel}${statusMenuCount}${isSelected ? ' (selected)' : ''}`}
      icon={
        <BenchlingIcon
          icon={isSelected ? 'square-check' : 'square-outline'}
          className={classes.icon}
          aria-hidden={true}
        />
      }
      onClick={() => {
        setIsSelected(!isSelected);
      }}
      {...getTestProps(dataTestAttrs)}
    >
      {statusLabel}
      {statusMenuCount}
    </Menu.Item>
  );
}
